import { apiFetch } from './ApiFetch';
import { BASE_URL } from './constants';
import { getUserSessionCookie } from '../utils/SessionCookie';
import {
  AccountProspectScrapeQuery,
  ScrapeJobResponse,
  ScrapeJobStatus,
  AccountProspectResponse,
  AccountSearchJobResponse,
  AccountSearchJobStatus,
  LeadSearchResult,
  LeadSearchJobResponse,
  LeadSearchJobStatus,
  LeadProspectQuery,
  NewsSearchRequest,
  NewsApiResponse,
  RegistryProspectQuery,
  RegistryJobCreateResponse,
  RegistryJobStatusResponse,
  RegistryJobResultResponse
} from '../types/prospects';

export const getAccountProspectSearchParams = async () => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/account/funding/params`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
};

export const searchAccountProspects = async (
  query: {
    industries: string[];
    countries?: string[] | null;
    size_ranges?: string[] | null;
    last_fundraising_date?: string | null;
    last_fundraising_series?: string[] | null;
  },
  limit: number
) => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/account/funding/search?limit=${limit}`;

  const res = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(query)
  });

  const data = await res.json();

  if (!res.ok) {
    if (res.statusText) {
      throw new Error(`${res.statusText}`);
    } else {
      throw new Error(`HTTP Error! status: ${res.status}`);
    }
  }

  return data;
};

// New background job API functions
export const searchAccountProspectsBackground = async (
  query: {
    industries: string[];
    countries?: string[] | null;
    size_ranges?: string[] | null;
    last_fundraising_date?: string | null;
    last_fundraising_series?: string[] | null;
  },
  limit: number
): Promise<AccountSearchJobResponse> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/account/funding/search/background?limit=${limit}`;

  const res = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(query)
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(`${res.statusText}`);
  }

  return data;
};

export const getAccountSearchJobStatus = async (
  jobId: string
): Promise<AccountSearchJobStatus> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/account/funding/search/${jobId}/status`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  return res.json();
};

export const getAccountSearchJobResult = async (
  jobId: string
): Promise<AccountProspectResponse[]> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/account/funding/search/${jobId}/result`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  return res.json();
};

export const scrapeWebsite = async (
  query: AccountProspectScrapeQuery
): Promise<ScrapeJobResponse> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/websites-scrape`;

  const res = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify({
      website_url: query.website_url,
      prompt: query.prompt.trim()
    })
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
};

export const getScrapingJobStatus = async (
  jobId: string
): Promise<ScrapeJobStatus> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/websites-scrape/${jobId}/status`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
};

export const getScrapingJobResult = async (jobId: string): Promise<any[]> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/websites-scrape/${jobId}/result`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
};

export async function startNewsSearch(
  body: NewsSearchRequest
): Promise<NewsApiResponse> {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/search/news`;

  const res = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  const data = await res.json();
  return { results: Array.isArray(data?.results) ? data.results : [] };
}

export async function startRegistrySearchBackground(
  body: RegistryProspectQuery
): Promise<RegistryJobCreateResponse | { noResults: true }> {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/sec_registry/search/start`;

  const res = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(body)
  });

  // 204 means backend prefetch found no SEC items; don't parse JSON
  if (res.status === 204) {
    return { noResults: true as const };
  }

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
}

export async function getRegistrySearchJobStatus(
  jobId: string
): Promise<RegistryJobStatusResponse> {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/sec_registry/search/status/${jobId}`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
}

export async function getRegistrySearchJobResult(
  jobId: string
): Promise<RegistryJobResultResponse> {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/sec_registry/search/result/${jobId}`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
}

export const getLeadProspectSearchParams = async () => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/lead/search/params`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  if (!res.ok) {
    throw new Error(`HTTP Error! status: ${res.status}`);
  }

  return res.json();
};

// New background job API functions
export const searchLeadProspectsBackground = async (
  query: LeadProspectQuery,
  limit: number
): Promise<LeadSearchJobResponse> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/lead/search/background?limit=${limit}`;

  const res = await apiFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    },
    body: JSON.stringify(query)
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(`${res.statusText}`);
  }

  return data;
};

export const getLeadSearchJobStatus = async (
  jobId: string
): Promise<LeadSearchJobStatus> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/lead/search/${jobId}/status`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  return res.json();
};

export const getLeadSearchJobResult = async (
  jobId: string
): Promise<LeadSearchResult[]> => {
  const sessionCookie = getUserSessionCookie();
  const url = `${BASE_URL}/prospect/lead/search/${jobId}/result`;

  const res = await apiFetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionCookie && { Authorization: `Bearer ${sessionCookie}` })
    }
  });

  return res.json();
};
